import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Directive, ElementRef, Input, inject } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, filter, fromEvent, switchMap } from 'rxjs';

@Directive({
  selector: '[flAutoGrow]',
  exportAs: 'autogrow',
  standalone: true,
})
export class AutoGrowDirective implements OnInit, OnChanges, OnDestroy {
  element = inject<ElementRef<HTMLElement>>(ElementRef);
  private time = inject(TimeUtils);

  @Input()
  set flAutoGrow(formControl: FormControl<string> | undefined) {
    this.formControlSubject$.next(formControl);
  }

  /**
   * When true, animates the height changes of the textarea.
   * Ensure a CSS transition is applied to the textarea.
   */
  @Input() flAutoGrowAnimate = false;

  /**
   * Maximum height (in pixels) the element will automatically grow to.
   * Can still be expanded manually beyond the max height.
   *
   * The minimum height is always the default height based on a textarea's `rows`.
   */
  @Input() flAutoGrowMaxHeight = 72;

  private formControlSubject$ = new BehaviorSubject<
    FormControl<string> | undefined
  >(undefined);
  private valueChangeSubscription?: Subscription;

  private heightGrowAnimFrameId: number;

  ngOnChanges(changes: SimpleChanges): void {
    if ('flAutoGrow' in changes) {
      this.formControlSubject$.next(changes.flAutoGrow.currentValue);
    }

    if ('flAutoGrowMaxHeight' in changes) {
      const { currentValue, previousValue } = changes.flAutoGrowMaxHeight;

      // Only adjust if max height changes
      if (isDefined(previousValue) && currentValue < previousValue) {
        this.time.setTimeout(() => this.adjust('shrink'));
      } else if (isDefined(previousValue) && currentValue > previousValue) {
        this.time.setTimeout(() => this.adjust('grow'));
      }
    }
  }

  ngOnInit(): void {
    const textArea = this.resolveTextArea();

    this.valueChangeSubscription = this.formControlSubject$
      .pipe(
        filter(isDefined),
        // Native 'input' events fire on every keystroke regardless of the
        // FormControl's updateOn setting, so valueChanges is not needed.
        switchMap(() => fromEvent(textArea, 'input')),
      )
      .subscribe(() => this.adjust());

    const formControl = this.formControlSubject$.value;
    if (formControl?.value) {
      // Wait for the textarea to render the scroll bar before adjusting
      this.time.setTimeout(() => this.adjust());
    }
  }

  public adjust(adjust?: 'shrink' | 'grow'): void {
    const textArea = this.resolveTextArea();

    let borderOffset = textArea.offsetHeight - textArea.clientHeight;

    // Check if we need to force adjust due to max height change
    if (
      adjust === 'shrink' &&
      textArea.clientHeight > this.flAutoGrowMaxHeight
    ) {
      textArea.style.height = `${Math.min(
        textArea.scrollHeight + borderOffset,
        this.flAutoGrowMaxHeight,
      )}px`;
    }
    if (adjust === 'grow' && textArea.clientHeight < this.flAutoGrowMaxHeight) {
      textArea.style.height = `${Math.min(
        textArea.scrollHeight + borderOffset,
        this.flAutoGrowMaxHeight,
      )}px`;
    }

    if (
      // if element actually exists and is rendered
      textArea.scrollHeight > 0
    ) {
      if (this.flAutoGrowAnimate) {
        let targetHeight: number;

        // Use native scrollHeight if overflowing.
        // Otherwise, use 'auto' to find the true height to handle shrinking
        if (textArea.scrollHeight > textArea.clientHeight) {
          targetHeight = textArea.scrollHeight + borderOffset;
        } else {
          const currentScrollTop = textArea.scrollTop;
          const currentHeightInline = textArea.style.height;

          // Temporarily collapse to measure true content height
          textArea.style.transition = 'none';
          textArea.style.overflow = 'hidden';
          textArea.style.height = 'auto';

          borderOffset = textArea.offsetHeight - textArea.clientHeight;
          targetHeight = textArea.scrollHeight + borderOffset;

          // Restore inline styles and scroll position
          textArea.style.height = currentHeightInline;
          textArea.style.overflow = 'auto';
          if (targetHeight > this.flAutoGrowMaxHeight) {
            textArea.scrollTop = currentScrollTop;
          }
        }

        // If height is unchanged, do nothing to allow the browser
        // to natively follow the caret when at max height
        const toHeight = Math.min(targetHeight, this.flAutoGrowMaxHeight);
        const fromHeight = parseFloat(getComputedStyle(textArea).height);

        if (Math.abs(fromHeight - toHeight) < 1) {
          return;
        }

        textArea.style.transition = 'none';
        textArea.style.height = `${fromHeight}px`;
        textArea.style.overflowY = 'hidden';

        // Lock scroll to top if under max height so bottom border animates smoothly
        if (targetHeight <= this.flAutoGrowMaxHeight) {
          textArea.scrollTop = 0;
        }

        this.heightGrowAnimFrameId = requestAnimationFrame(() => {
          this.heightGrowAnimFrameId = 0;
          textArea.style.transition = '';
          textArea.style.height = `${toHeight}px`;

          if (targetHeight <= this.flAutoGrowMaxHeight) {
            textArea.scrollTop = 0;
          }

          if (fromHeight !== toHeight) {
            const onTransitionEnd = (): void => {
              textArea.style.overflow = 'auto';
              textArea.removeEventListener('transitionend', onTransitionEnd);
            };
            textArea.addEventListener('transitionend', onTransitionEnd);
          } else {
            textArea.style.overflow = 'auto';
          }
        });
      } else {
        // Snap to text to determine the proper height
        textArea.style.overflow = 'hidden';
        textArea.style.height = 'auto';
        textArea.style.overflowY = 'auto';

        // Borders aren't counted in the scrollHeight or clientHeight,
        // but are in the `style.height`, so we need to add the extra height manually
        borderOffset = textArea.offsetHeight - textArea.clientHeight;
        textArea.style.height = `${Math.min(
          textArea.scrollHeight + borderOffset,
          this.flAutoGrowMaxHeight,
        )}px`;

        // Undo styling/snap changes
        textArea.style.overflow = 'auto';
      }
    }
  }

  private resolveTextArea(): HTMLElement {
    if (this.element.nativeElement instanceof HTMLTextAreaElement) {
      return this.element.nativeElement;
    }
    if (this.element.nativeElement.tagName === 'FL-TEXTAREA') {
      return this.element.nativeElement.children.item(0) as HTMLElement;
    }
    throw new Error('Not supported element!');
  }

  ngOnDestroy(): void {
    this.valueChangeSubscription?.unsubscribe();
    if (this.heightGrowAnimFrameId) {
      cancelAnimationFrame(this.heightGrowAnimFrameId);
    }
  }
}
